import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../stores';
import { setHelpTooltipVisible } from '../stores/UserStore';
import { shouldExpandGameControls } from '../utils/gameControlsDefault';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Fade,
  styled,
} from '@mui/material';
import {
  Help as HelpIcon,
  Close as CloseIcon,
  Keyboard as KeyboardIcon,
  TouchApp as TouchAppIcon,
  Mouse as MouseIcon,
} from '@mui/icons-material';
import { BOTTOM_RIGHT_EDGE, BOTTOM_RIGHT_GAP, TOGGLE_SIZE } from './bottomRightControls';

// Styled components for the help tooltip
const TooltipContainer = styled(Box)(() => ({
  position: 'fixed',
  bottom: BOTTOM_RIGHT_EDGE,
  right: BOTTOM_RIGHT_EDGE,
  zIndex: 1000,
  pointerEvents: 'auto',
}));

// The open panel rises above the toggle (and the feedback button beside it), so the toggle stays put
// and closes it again.
const PanelAnchor = styled(Box)(() => ({
  position: 'absolute',
  right: 0,
  bottom: `calc(100% + ${BOTTOM_RIGHT_GAP}px)`,
  // Growing upwards from the corner, a short window would push the top of the panel off screen.
  maxHeight: `calc(100vh - ${BOTTOM_RIGHT_EDGE * 2 + TOGGLE_SIZE + BOTTOM_RIGHT_GAP}px)`,
  overflowY: 'auto',
}));

const TooltipPaper = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #fff2d6 100%)',
  border: '1px solid rgba(249, 116, 21, 0.2)',
  borderRadius: '12px',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 4px 14px 0 rgba(249, 116, 21, 0.3)',
  overflow: 'hidden',
  maxWidth: 320,
  minWidth: 280,
}));

const TooltipHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  background: 'linear-gradient(135deg, #f97415 0%, #ffa047 100%)',
  borderBottom: '1px solid rgba(249, 116, 21, 0.3)',
}));

const TooltipTitle = styled(Typography)(({ theme }) => ({
  color: '#ffffff !important',
  fontWeight: 600,
  fontSize: '0.95rem',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const TooltipContent = styled(Box)(({ theme }) => ({
  padding: '16px',
}));

const ControlSection = styled(Box)(({ theme }) => ({
  marginBottom: '16px',
  '&:last-child': {
    marginBottom: 0,
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#f97415',
  fontWeight: 600,
  fontSize: '0.85rem',
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
}));

const ControlRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '6px',
  '&:last-child': {
    marginBottom: 0,
  },
}));

const ControlKey = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  border: '1px solid rgba(249, 116, 21, 0.3)',
  borderRadius: '4px',
  padding: '4px 8px',
  fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#212121',
  minWidth: '24px',
  textAlign: 'center',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
}));

const ControlDescription = styled(Typography)(({ theme }) => ({
  color: '#616161',
  fontSize: '0.8rem',
  flex: 1,
  marginLeft: '12px',
}));

const ToggleButton = styled(IconButton)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #fff2d6 100%)',
  border: '1px solid rgba(249, 116, 21, 0.2)',
  borderRadius: '50%',
  width: `${TOGGLE_SIZE}px`,
  height: `${TOGGLE_SIZE}px`,
  color: '#f97415',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 4px 14px 0 rgba(249, 116, 21, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #fff2d6 0%, #ffe7a3 100%)',
    borderColor: 'rgba(249, 116, 21, 0.4)',
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 10px 25px 0 rgba(249, 116, 21, 0.4)',
  },
}));

interface HelpTooltipProps {
  onToggleFromMenu?: () => void;
}

const HelpTooltip: React.FC<HelpTooltipProps> = ({ onToggleFromMenu }) => {
  const dispatch = useDispatch();
  const isVisible = useSelector((state: RootState) => state.user.helpTooltipVisible);
  const roomJoined = useSelector((state: RootState) => state.room.roomJoined);
  const userId = useSelector((state: RootState) => state.user.userData?.id);
  const createdAt = useSelector((state: RootState) => state.user.userData?.createdAt);

  // Show whenever in a room, like GameMenu
  const shouldShow = roomJoined;

  // On the first room join only, start expanded for an account's first day and collapsed after.
  // Once per user, so a panel someone closed stays closed when they change rooms.
  const decidedForUser = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const user = userId ?? null;
    if (!roomJoined || decidedForUser.current === user) return;
    decidedForUser.current = user;
    dispatch(setHelpTooltipVisible(shouldExpandGameControls(createdAt)));
  }, [roomJoined, userId, createdAt, dispatch]);

  const handleToggle = () => {
    dispatch(setHelpTooltipVisible(!isVisible));
    if (onToggleFromMenu) {
      onToggleFromMenu();
    }
  };

  const handleClose = () => {
    dispatch(setHelpTooltipVisible(false));
  };

  // Auto-hide after 10 seconds if opened from menu
  useEffect(() => {
    if (isVisible && onToggleFromMenu) {
      const timer = setTimeout(() => {
        dispatch(setHelpTooltipVisible(false));
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onToggleFromMenu, dispatch]);

  if (!shouldShow) return null;

  return (
    <TooltipContainer data-testid="help-tooltip">
      {isVisible && (
        <PanelAnchor>
          <Fade in={isVisible} timeout={300}>
            <TooltipPaper>
              <TooltipHeader>
                <TooltipTitle>
                  <HelpIcon sx={{ fontSize: '1rem' }} />
                  Game Controls
                </TooltipTitle>
                <IconButton
                  size="small"
                  onClick={handleClose}
                  sx={{
                    color: '#ffffff',
                    '&:hover': { 
                      color: '#ffffff',
                      background: 'rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </TooltipHeader>
            
              <TooltipContent>
                <Box sx={{ 
                  background: 'rgba(249, 116, 21, 0.1)', 
                  border: '1px solid rgba(249, 116, 21, 0.2)', 
                  borderRadius: '8px', 
                  padding: '12px', 
                  marginBottom: '16px',
                  fontSize: '0.85rem',
                  color: '#616161'
                }}>
                  💡 <strong>Tip:</strong> You can use either WASD keys or arrow keys to move - whichever feels more comfortable for you!
                </Box>
                <ControlSection>
                  <SectionTitle>
                    <KeyboardIcon sx={{ fontSize: '0.8rem' }} />
                    Move Your Character
                  </SectionTitle>
                  <ControlRow>
                    <ControlKey>W</ControlKey>
                    <ControlDescription>Move up (forward)</ControlDescription>
                  </ControlRow>
                  <ControlRow>
                    <ControlKey>A</ControlKey>
                    <ControlDescription>Move left</ControlDescription>
                  </ControlRow>
                  <ControlRow>
                    <ControlKey>S</ControlKey>
                    <ControlDescription>Move down (backward)</ControlDescription>
                  </ControlRow>
                  <ControlRow>
                    <ControlKey>D</ControlKey>
                    <ControlDescription>Move right</ControlDescription>
                  </ControlRow>
                  <ControlRow>
                    <ControlKey>↑</ControlKey>
                    <ControlDescription>Move up (arrow key)</ControlDescription>
                  </ControlRow>
                  <ControlRow>
                    <ControlKey>↓</ControlKey>
                    <ControlDescription>Move down (arrow key)</ControlDescription>
                  </ControlRow>
                  <ControlRow>
                    <ControlKey>←</ControlKey>
                    <ControlDescription>Move left (arrow key)</ControlDescription>
                  </ControlRow>
                  <ControlRow>
                    <ControlKey>→</ControlKey>
                    <ControlDescription>Move right (arrow key)</ControlDescription>
                  </ControlRow>
                </ControlSection>

                <ControlSection>
                  <SectionTitle>
                    <TouchAppIcon sx={{ fontSize: '0.8rem' }} />
                    Interact with Objects
                  </SectionTitle>
                  <ControlRow>
                    <ControlKey>E</ControlKey>
                    <ControlDescription>Sit on chairs and interact with items</ControlDescription>
                  </ControlRow>
                  <ControlRow>
                    <ControlKey>R</ControlKey>
                    <ControlDescription>Use computers and other devices</ControlDescription>
                  </ControlRow>
                </ControlSection>

                <ControlSection>
                  <SectionTitle>
                    <MouseIcon sx={{ fontSize: '0.8rem' }} />
                    Menu & Navigation
                  </SectionTitle>
                  <ControlRow>
                    <ControlKey>TAB</ControlKey>
                    <ControlDescription>Open/close main menu</ControlDescription>
                  </ControlRow>
                  <ControlRow>
                    <ControlKey>ESC</ControlKey>
                    <ControlDescription>Close menu and dialogs</ControlDescription>
                  </ControlRow>
                </ControlSection>
              </TooltipContent>
            </TooltipPaper>
          </Fade>
        </PanelAnchor>
      )}
      <ToggleButton
        onClick={handleToggle}
        title={isVisible ? 'Hide Game Controls' : 'Show Game Controls'}
        aria-label={isVisible ? 'Hide Game Controls' : 'Show Game Controls'}
        aria-expanded={isVisible}
      >
        <HelpIcon />
      </ToggleButton>
    </TooltipContainer>
  );
};

export default HelpTooltip;
